import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React from 'react';
import { Linking, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { WcNewsArticle } from '@/lib/data/provider';
import { formatMatchDate, relativeTimeParts } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** Relative "2h ago" label, falling back to an absolute date for older items. */
function useTimeAgo(publishedAt: string | null): string | null {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  if (!publishedAt) return null;
  const date = new Date(publishedAt);
  if (Number.isNaN(date.getTime())) return null;
  const parts = relativeTimeParts(date);
  if (!parts) return formatMatchDate(date, numeralOptions);
  if (parts.key === 'now') return t('news.timeNow');
  return t(`news.time.${parts.key}`, { count: parts.count });
}

/**
 * A World Cup news card: cover image (with a graceful fallback), a 2-3 line
 * headline, and a source · relative-time footer. Tapping opens the publisher in
 * the browser. The whole card is RTL-safe (flex flips with the writing system)
 * and themed via palette tokens only.
 *
 * `compact` drops the cover image for tighter, in-screen rails (the match
 * screen) where a full-bleed photo per item would be too heavy.
 */
export function NewsCard({
  article,
  compact = false,
}: {
  article: WcNewsArticle;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const timeAgo = useTimeAgo(article.published_at);
  const hasImage = !compact && Boolean(article.image_url);

  const open = () => {
    const url = article.url;
    // Only follow http(s) links; never hand the OS a javascript:/intent:/file:
    // scheme from upstream data.
    if (!url || !/^https?:\/\//i.test(url)) return;
    Linking.openURL(url).catch(() => {
      // Non-fatal: nothing to do if no browser can open the link.
    });
  };

  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={article.title ?? t('news.wcTitle')}
      onPress={open}
      className="active:opacity-90"
    >
      <Card className="overflow-hidden p-0">
        {hasImage ? (
          <View className="bg-surface-2">
            <Image
              source={{ uri: article.image_url ?? undefined }}
              style={{ width: '100%', height: 168 }}
              contentFit="cover"
              transition={150}
              cachePolicy="memory-disk"
              accessibilityIgnoresInvertColors
            />
            {article.is_magazine ? (
              <View className="absolute left-3 top-3 flex-row items-center gap-1 rounded-full bg-volt px-2 py-0.5">
                <Ionicons name="bookmark" size={11} color={palette.onVolt} />
                <AppText variant="micro" weight="bold" style={{ color: palette.onVolt }}>
                  {t('news.magazine')}
                </AppText>
              </View>
            ) : null}
          </View>
        ) : null}

        <View className="gap-2 px-4 py-3.5">
          <AppText
            variant="caption"
            weight="bold"
            className="leading-5 text-ink"
            numberOfLines={3}
          >
            {article.title ?? ''}
          </AppText>

          <View className="flex-row items-center justify-between pt-0.5">
            <View className="min-w-0 flex-1 flex-row items-center gap-1.5">
              <View className="h-1.5 w-1.5 rounded-full bg-volt" />
              {article.source_name ? (
                <AppText
                  variant="micro"
                  weight="bold"
                  className="shrink uppercase tracking-wide text-volt-bright"
                  numberOfLines={1}
                >
                  {article.source_name}
                </AppText>
              ) : null}
              {timeAgo ? (
                <AppText variant="micro" className="shrink-0 text-muted" numberOfLines={1}>
                  {article.source_name ? '· ' : ''}
                  {timeAgo}
                </AppText>
              ) : null}
            </View>
            <Ionicons name="open-outline" size={14} color={palette.muted} />
          </View>
        </View>
      </Card>
    </Pressable>
  );
}
